import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';

const LOGIN_ERROR_STRINGS = appStrings.modules.login.error.login;

export type RegistrationValues = Readonly<{
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  password: string;
  invite_code?: string | null;
  is_fourteen_plus: boolean;
  register_id: string | null;
  grade?: string;
  school?: string;
  zip_code?: string;
  onboarding_type?: string | null;
}>;

const headersWithGuestToken = (guestToken?: string): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (guestToken) headers.Authorization = `Bearer ${guestToken}`;

  return headers;
};

export function handleEmailLogin(
  email: string,
  password: string,
  environmentUrl: string,
  guestToken?: string,
) {
  return fetch(`${environmentUrl}/users/login`, {
    method: 'POST',
    headers: headersWithGuestToken(guestToken),
    body: JSON.stringify({ email, password }),
  });
}

export function handlePhoneLogin(phone: string, environmentUrl: string) {
  return fetch(`${environmentUrl}/otp/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone }),
  });
}

export function handleOtpVerify(
  code: string,
  phone: string,
  environmentUrl: string,
  guestToken?: string,
) {
  return fetch(`${environmentUrl}/otp/login`, {
    method: 'POST',
    headers: headersWithGuestToken(guestToken),
    body: JSON.stringify({ code, phone }),
  });
}

export function handleBasicRegistration(
  payload: RegistrationValues,
  environmentUrl: string,
  guestToken?: string,
) {
  return fetch(`${environmentUrl}/users/register`, {
    method: 'POST',
    headers: headersWithGuestToken(guestToken),
    body: JSON.stringify({ ...payload, accepted_terms: true }),
  });
}

export async function handleBasicRegistrationWithError(
  payload: RegistrationValues,
  environmentUrl: string,
): Promise<{
  status: number;
  token: string;
  user: BackrsUser;
}> {
  const response = await fetch(`${environmentUrl}/users/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, accepted_terms: true }),
  });

  if (!response.ok) {
    const errorBody = await response.json();

    throw {
      status: response.status,
      response: errorBody, // <== this has your { errors: [...] }
    };
  }

  const responseJson = await response.json();

  return {
    status: response.status,
    token: responseJson.token,
    user: responseJson.user,
  };
}

export function sendPasswordReset(email: string, environmentUrl: string, source: string) {
  return fetch(`${environmentUrl}/users/send-password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // 'source' identifies the originating app so the reset link returns there.
    body: JSON.stringify({ email, source }),
  });
}

export type PasswordResetError = Readonly<{
  status: number;
  response: { errors?: string[]; message?: string };
}>;

/**
 * Sets a new password using the reset token from the email link.
 * Resolves on a successful (2xx) response; throws a `PasswordResetError` on
 * failure, where `response` is the parsed error body when available.
 */
export async function resetPassword(
  password: string,
  confirmPassword: string,
  token: string,
  environmentUrl: string,
) {
  const decodedToken = decodeURIComponent(token);

  const response = await fetch(`${environmentUrl}/users/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${decodedToken}`,
    },
    body: JSON.stringify({ password, confirmPassword }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));

    const error: PasswordResetError = {
      status: response.status,
      response: errorBody,
    };

    throw error;
  }
}

export function verifyTempToken(token: string, environmentUrl: string) {
  const decodedToken = decodeURIComponent(token);

  return fetch(`${environmentUrl}/users/crossdomain`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${decodedToken}`,
    },
  });
}

export const decodeToken = (tok: string) => {
  const base64Url = tok.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const getTokenExpirationDate = (tok: string) => new Date(decodeToken(tok).exp * 1000);

export const ENVIRONMENTS = {
  local: 'local',
  dev: 'development',
  staging: 'staging',
  stg: 'stg',
  prod: 'production',
  prd: 'prd',
};

export const computeEnvironment = (environment: string) => {
  const env = environment || ENVIRONMENTS.local;

  return env;
};

export async function handleAutoLogin(
  access_token: string,
  environmentUrl: string,
): Promise<BackrsUser | null> {
  const response = await fetch(`${environmentUrl}/users/auto-login`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.ok) {
    const jsonUser = await response.json();

    const user: BackrsUser = {
      ...jsonUser,
      access_token,
      birthday: jsonUser.birthday
        ? new Date(jsonUser.birthday).toISOString().split('T')[0]
        : undefined,
    };

    return user;
  }

  return null;
}

type HandleLoggingInProps = Readonly<{
  phone: string;
  email: string;
  password: string;
  environmentUrl: string;
  afterLogin: (loginResponse: { access_token: string | undefined }) => Promise<void>;
  setErrorMessage: (error: string | undefined) => void;
  guestToken?: string;
}>;

export const handleLoggingIn = async ({
  phone,
  email,
  password,
  environmentUrl,
  afterLogin,
  setErrorMessage,
  guestToken,
}: HandleLoggingInProps) => {
  const res = phone
    ? await handlePhoneLogin(phone, environmentUrl)
    : await handleEmailLogin(email, password, environmentUrl, guestToken);

  if (!res.ok) {
    if (res.status === 503) {
      setErrorMessage('Service is currently unavailable. Please try again later.');
    }

    setErrorMessage(phone ? LOGIN_ERROR_STRINGS.phone : LOGIN_ERROR_STRINGS.userNameOrPassword);

    return;
  }

  const loginResponse = await res.json();
  const loginObject = !phone ? loginResponse : { ...loginResponse, phone };

  return afterLogin && (await afterLogin(loginObject));
};

type HandleUserRegistrationProps = Readonly<{
  payload: RegistrationValues;
  environmentUrl: string;
  afterRegistration: () => void;
  setErrorMessage: (error: string | undefined) => void;
  guestToken?: string;
}>;

export const handleUserRegistration = async ({
  payload,
  environmentUrl,
  afterRegistration,
  setErrorMessage,
  guestToken,
}: HandleUserRegistrationProps) => {
  const res = await handleBasicRegistration(payload, environmentUrl, guestToken);

  if (!res.ok) {
    setErrorMessage('Error registering user');

    return;
  }

  return afterRegistration && afterRegistration();
};

function computeDomain(hostname: string): string {
  const parts = hostname.split('.');
  const len = parts.length;

  // Check for .backrs.com (e.g., app.backrs.com)
  if (len >= 2 && parts[len - 2] === 'backrs' && parts[len - 1] === 'com') {
    // sandbox gets its own scope so its token never collides with production's
    if (len >= 3 && parts[len - 3] === 'sandbox') {
      return '.sandbox.backrs.com';
    }

    return '.backrs.com';
  }

  // Check for *.backrs-dev.com (e.g., dev.backrs-dev.com, sub.stg.backrs-dev.com)
  if (len >= 3 && parts[len - 2] === 'backrs-dev' && parts[len - 1] === 'com') {
    const subDomain = parts[len - 3];

    // Ensure the subdomain is one of the known environments
    // This prevents matching something like 'random.backrs-dev.com'
    if (['dev', 'staging', 'stg', 'prd', 'sandbox'].includes(subDomain)) {
      return `.${subDomain}.backrs-dev.com`;
    }
  }

  // Default for localhost or other unknown hostnames
  return '.app.localhost';
}

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export const setCookie = (token: string, environment: string) => {
  const env = computeEnvironment(environment);
  // Access tokens have no exp claim — without a valid expires date the browser
  // treats this as a session cookie and drops it when the browser closes
  const { exp } = decodeToken(token);
  const expirationDate = exp ? new Date(exp * 1000) : new Date(Date.now() + ONE_YEAR_MS);
  const secureTag = env !== 'local' ? '; secure' : '';

  document.cookie = `token=${encodeURIComponent(token)}; domain=${computeDomain(window.location.hostname)}; path=/; expires=${expirationDate.toUTCString()}${secureTag}`;
};

export const getCookie = () => {
  const cookie = document.cookie.split('; ').find((row) => row.startsWith('token='));

  if (!cookie) {
    return null;
  }

  const value = cookie.split('=')[1];

  return decodeURIComponent(value);
};

export const removeCookie = () => {
  document.cookie = `token=; path=/; domain=${computeDomain(window.location.hostname)}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

type TokenType = 'backrs_ui_token';

export const getAccessToken = (tokenType: TokenType): string | null => {
  const accessToken = localStorage.getItem(tokenType);

  if (!accessToken) {
    return null;
  }

  return accessToken;
};

export const setAccessToken = (tokenType: TokenType, token: string): void => {
  localStorage.setItem(tokenType, token);
};

export const deleteAccessToken = (tokenType: TokenType): void => {
  localStorage.removeItem(tokenType);
};

export const getAccessTokenOrCookie = (
  tokenType: TokenType,
  isUnifiedLoginDisabled = false,
): string | null => {
  const cookie = getCookie();

  if (!cookie && !isUnifiedLoginDisabled) {
    return null;
  }

  return getAccessToken(tokenType) || cookie;
};
